import { create } from 'zustand';
import { fetchWithAuth } from '@/lib/api';

interface BatchProgress {
  batchId: string;
  type: string; // 'scp-image' | 'card-stats' | 'card-images'
  label: string;
  status: 'running' | 'completed' | 'cancelled' | 'error';
  total: number;
  done: number;
  success: number;
  failed: number;
  current: string;
  extra?: Record<string, any>;
}

interface BatchState {
  jobs: Map<string, BatchProgress>;
  startPolling: (batchId: string, type: string, label: string, progressUrl: string) => void;
  stopPolling: (batchId: string) => void;
  clear: (batchId: string) => void;
  clearAll: () => void;
  getRunning: () => BatchProgress[];
}

const pollTimers = new Map<string, ReturnType<typeof setInterval>>();

export const useBatchStore = create<BatchState>((set, get) => ({
  jobs: new Map(),

  startPolling: (batchId: string, type: string, label: string, progressUrl: string) => {
    // 기존 같은 ID 폴링 정리
    const existing = pollTimers.get(batchId);
    if (existing) clearInterval(existing);

    set(state => {
      const jobs = new Map(state.jobs);
      jobs.set(batchId, { batchId, type, label, status: 'running', total: 0, done: 0, success: 0, failed: 0, current: '' });
      return { jobs };
    });

    const timer = setInterval(async () => {
      try {
        const res = await fetchWithAuth(progressUrl);
        if (!res.ok) {
          // 서버 재시작 등으로 배치 상태 유실 → error 처리
          set(state => {
            const jobs = new Map(state.jobs);
            const existing = jobs.get(batchId);
            if (existing) jobs.set(batchId, { ...existing, status: 'error', current: '서버 연결 끊김 (재시작됨)' });
            return { jobs };
          });
          get().stopPolling(batchId);
          return;
        }

        const json = await res.json();
        set(state => {
          const jobs = new Map(state.jobs);
          jobs.set(batchId, { batchId, type, label, ...json.data });
          return { jobs };
        });

        if (json.data.status !== 'running') {
          get().stopPolling(batchId);
        }
      } catch {
        set(state => {
          const jobs = new Map(state.jobs);
          const existing = jobs.get(batchId);
          if (existing) jobs.set(batchId, { ...existing, status: 'error', current: '서버 연결 끊김' });
          return { jobs };
        });
        get().stopPolling(batchId);
      }
    }, 3000);

    pollTimers.set(batchId, timer);
  },

  stopPolling: (batchId: string) => {
    const timer = pollTimers.get(batchId);
    if (timer) { clearInterval(timer); pollTimers.delete(batchId); }
    // 완료/에러 상태 job을 10분 후 자동 정리
    setTimeout(() => {
      const job = get().jobs.get(batchId);
      if (job && job.status !== 'running') {
        get().clear(batchId);
      }
    }, 10 * 60 * 1000);
  },

  clear: (batchId: string) => {
    get().stopPolling(batchId);
    set(state => {
      const jobs = new Map(state.jobs);
      jobs.delete(batchId);
      return { jobs };
    });
  },

  clearAll: () => {
    pollTimers.forEach(timer => clearInterval(timer));
    pollTimers.clear();
    set({ jobs: new Map() });
  },

  getRunning: () => {
    return Array.from(get().jobs.values()).filter(j => j.status === 'running');
  },
}));
